import { round2, applyFreeDelivery } from "./pricing";

/**
 * Como cupom e taxa de entrega se combinam (decisão do Pedro, 22/09/2026).
 *
 * UMA conta só, usada pelo servidor (criação do pedido) e pelo checkout
 * (prévia): se os dois divergirem, o cliente vê um total e paga outro.
 *
 * Ordem — o valor cobrado depende dela:
 *  1) pedido mínimo confere o subtotal CRU (fora daqui: é checagem, não conta);
 *  2) parte do desconto que cai nos PRODUTOS: percentual sobre o subtotal,
 *     fixo até o subtotal, cupom de frete = 0;
 *  3) frete grátis pelo limiar decide sobre a base que a loja escolheu
 *     (subtotal cheio, ou já descontado dos produtos) e sai a taxa REAL;
 *  4) só então o desconto fecha na ENTREGA: cupom de frete = a taxa inteira;
 *     "abate entrega" ligado = percentual da taxa + o que sobrou do fixo,
 *     até a taxa;
 *  5) acréscimo de cartão fica fora daqui, sobre produtos líquidos + entrega
 *     LÍQUIDA — o chamador usa netProducts/netDelivery.
 *
 * Não trabalhar com max(subtotal - desconto, 0) + taxa: quebra quando o
 * desconto passa do subtotal. Produtos líquidos e entrega líquida vivem
 * separados e só se somam no fim.
 */
export type CouponDiscountType = "percent" | "fixed" | "free_delivery";

export interface CouponLike {
  discountType: CouponDiscountType | string;
  discountValue: number | string;
}

export interface CouponMathArgs {
  /** Subtotal dos produtos (já líquido de promoção por quantidade). */
  subtotal: number;
  coupons: CouponLike[];
  deliveryMode: "entrega" | "retirada" | string | null | undefined;
  /** Taxa cotada ANTES do frete grátis. Em retirada/pendente, ignorada. */
  rawDeliveryFee: number;
  /** Frete "a calcular depois": não existe taxa pra zerar nem pra abater. */
  feePending?: boolean;
  freeDeliveryAbove: number | string | null | undefined;
  /** "Cupom não tira o frete grátis": limiar compara com o subtotal CHEIO. */
  freeDeliveryBeforeCoupon: boolean;
  /** "Desconto do cupom pode abater a entrega". */
  couponCoversDelivery: boolean;
}

export interface CouponMath {
  /** Parte do desconto que caiu nos produtos (≤ subtotal). */
  productDiscount: number;
  netProducts: number;
  /** Taxa REAL do pedido depois do frete grátis por limiar — é o que se grava. */
  deliveryFee: number;
  /** Frete grátis pelo limiar da loja. */
  freeApplied: boolean;
  /** Parte do desconto que cobre a entrega (≤ deliveryFee). */
  deliveryDiscount: number;
  netDelivery: number;
  /** productDiscount + deliveryDiscount — é o que se grava em coupon_discount. */
  couponDiscount: number;
  hasFreeDeliveryCoupon: boolean;
}

export function isFreeDeliveryCoupon(c: { discountType: string }): boolean {
  return c.discountType === "free_delivery";
}

export function computeCouponMath(args: CouponMathArgs): CouponMath {
  const subtotal = round2(Math.max(0, Number(args.subtotal) || 0));
  const entrega = args.deliveryMode === "entrega";
  const pending = entrega && args.feePending === true;

  // 2) parte dos produtos. Cada cupom é calculado sozinho contra o subtotal
  //    CHEIO (percentuais não compõem entre si); a soma é limitada ao subtotal
  //    e o que passa vira "sobra" — só o "abate entrega" a usa.
  let percentProducts = 0;
  let fixedTotal = 0;
  let hasFreeDeliveryCoupon = false;
  const percents: number[] = [];
  for (const c of args.coupons) {
    const v = Number(c.discountValue) || 0;
    if (c.discountType === "percent") {
      percentProducts += round2(subtotal * (v / 100));
      percents.push(v);
    } else if (c.discountType === "fixed") {
      fixedTotal += v;
    } else if (c.discountType === "free_delivery") {
      hasFreeDeliveryCoupon = true;
    }
  }
  const productDiscountRaw = round2(percentProducts + fixedTotal);
  const productDiscount = Math.min(subtotal, productDiscountRaw);
  const overflow = round2(Math.max(0, productDiscountRaw - subtotal));
  const netProducts = round2(subtotal - productDiscount);

  // 3) taxa real.
  let deliveryFee = 0;
  let freeApplied = false;
  if (entrega && !pending) {
    const base = args.freeDeliveryBeforeCoupon ? subtotal : netProducts;
    const r = applyFreeDelivery({
      deliveryFee: round2(Math.max(0, Number(args.rawDeliveryFee) || 0)),
      subtotalAfterDiscount: base,
      freeDeliveryAbove: args.freeDeliveryAbove,
    });
    deliveryFee = r.fee;
    freeApplied = r.freeApplied;
  }

  // 4) parte da entrega.
  let deliveryDiscount = 0;
  if (deliveryFee > 0) {
    if (hasFreeDeliveryCoupon) {
      deliveryDiscount = deliveryFee;
    } else if (args.couponCoversDelivery) {
      const percentOnFee = percents.reduce((s, p) => s + round2(deliveryFee * (p / 100)), 0);
      deliveryDiscount = Math.min(deliveryFee, round2(percentOnFee + overflow));
    }
  }
  const netDelivery = round2(deliveryFee - deliveryDiscount);

  return {
    productDiscount,
    netProducts,
    deliveryFee,
    freeApplied,
    deliveryDiscount,
    netDelivery,
    couponDiscount: round2(productDiscount + deliveryDiscount),
    hasFreeDeliveryCoupon,
  };
}

/**
 * Cupom de frete grátis sem nada pra zerar: em retirada (ou a combinar), com frete "a
 * calcular", ou quando a entrega já saiu grátis pelo limiar. Devolve a
 * mensagem de recusa (mesma nas duas validações) ou null quando tem efeito.
 * A recusa acontece ANTES da transação — não queima o uso do cupom.
 */
export function freeDeliveryCouponProblem(args: {
  deliveryMode: string | null | undefined;
  feePending?: boolean;
  /** Taxa real (depois do limiar) que o cupom zeraria. */
  deliveryFee: number;
}): string | null {
  if (args.deliveryMode === "retirada" || args.deliveryMode === "combinar") return "Este cupom de frete grátis vale só para entrega.";
  if (args.feePending || !(args.deliveryFee > 0)) return "Este cupom não tem efeito neste pedido.";
  return null;
}
